import { FC, useEffect, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useSnackbar } from 'notistack';
import { MergeProfileSave } from 'api/models/MergeProfileSave';
import { useMergeProfiles, useProfileCompanies, useProfileEdgarIssuerNames } from 'services/hooks/companies';
import { UNIQUE_FIELD_LABELS, UNIQUE_FIELDS } from './ProfileMergeDialog.constants';
import { MergeEditorState, ProfileMergeDialogProps } from './ProfileMergeDialog.props';

function invertDirection(dir: MergeEditorState['moveCompanies']): MergeEditorState['moveCompanies'] {
  if (dir === 'to_primary') return 'to_secondary';
  if (dir === 'to_secondary') return 'to_primary';
  return 'none';
}

const ProfileMergeDialog: FC<ProfileMergeDialogProps> = ({
  open,
  primaryProfile,
  secondaryProfile,
  conflictFields = [],
  onClose,
  onSuccess,
}) => {
  const mergeMutation = useMergeProfiles();
  const { enqueueSnackbar } = useSnackbar();

  const { data: primaryCompaniesData } = useProfileCompanies(primaryProfile.id);
  const { data: secondaryCompaniesData } = useProfileCompanies(secondaryProfile.id);
  const { data: primaryIssuersData } = useProfileEdgarIssuerNames(primaryProfile.id);
  const { data: secondaryIssuersData } = useProfileEdgarIssuerNames(secondaryProfile.id);

  // Track which profile is on which side. "primary" = left = the pk used in the API call.
  const [swapped, setSwapped] = useState(false);

  const leftProfile = swapped ? secondaryProfile : primaryProfile;
  const rightProfile = swapped ? primaryProfile : secondaryProfile;

  const leftCompanies = (swapped ? secondaryCompaniesData : primaryCompaniesData) ?? [];
  const rightCompanies = (swapped ? primaryCompaniesData : secondaryCompaniesData) ?? [];
  const leftIssuers = (swapped ? secondaryIssuersData : primaryIssuersData)?.results ?? [];
  const rightIssuers = (swapped ? primaryIssuersData : secondaryIssuersData)?.results ?? [];

  const [state, setState] = useState<MergeEditorState>(() => buildInitialState());

  function buildInitialState(): MergeEditorState {
    return {
      primaryFields: {
        name: primaryProfile.name ?? '',
        cap_iq_id: primaryProfile.cap_iq_id ?? '',
        sacra_domain: primaryProfile.sacra_domain ?? '',
        zanbato_id: primaryProfile.zanbato_id ?? '',
      },
      secondaryFields: {
        name: secondaryProfile.name ?? '',
        cap_iq_id: secondaryProfile.cap_iq_id ?? '',
        sacra_domain: secondaryProfile.sacra_domain ?? '',
        zanbato_id: secondaryProfile.zanbato_id ?? '',
      },
      moveCompanies: 'none',
      moveIssuerNames: 'none',
      deleteSecondary: false,
    };
  }

  useEffect(() => {
    if (open) {
      setSwapped(false);
      setState(buildInitialState());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, primaryProfile, secondaryProfile]);

  // Swap the two sides: flip field values, invert move directions, toggle swapped flag.
  const handleSwap = () => {
    setState(prev => ({
      ...prev,
      primaryFields: prev.secondaryFields,
      secondaryFields: prev.primaryFields,
      moveCompanies: invertDirection(prev.moveCompanies),
      moveIssuerNames: invertDirection(prev.moveIssuerNames),
    }));
    setSwapped(prev => !prev);
  };

  // Single atomic setState for field transfers to avoid batching surprises.
  const takeLeft = (field: string) => {
    setState(prev => {
      const rightVal = prev.secondaryFields[field as keyof typeof prev.secondaryFields];
      return {
        ...prev,
        primaryFields: { ...prev.primaryFields, [field]: rightVal },
        secondaryFields: { ...prev.secondaryFields, [field]: '' },
      };
    });
  };

  const takeRight = (field: string) => {
    setState(prev => {
      const leftVal = prev.primaryFields[field as keyof typeof prev.primaryFields];
      return {
        ...prev,
        secondaryFields: { ...prev.secondaryFields, [field]: leftVal },
        primaryFields: { ...prev.primaryFields, [field]: '' },
      };
    });
  };

  const toggleMoveCompanies = (direction: 'to_primary' | 'to_secondary') => {
    setState(prev => ({
      ...prev,
      moveCompanies: prev.moveCompanies === direction ? 'none' : direction,
    }));
  };

  const toggleMoveIssuerNames = (direction: 'to_primary' | 'to_secondary') => {
    setState(prev => ({
      ...prev,
      moveIssuerNames: prev.moveIssuerNames === direction ? 'none' : direction,
    }));
  };

  const handleSave = async () => {
    if (leftProfile.id == null) return;

    const payload: MergeProfileSave = {
      secondary_id: rightProfile.id!,
      primary_data: {
        name: state.primaryFields.name,
        cap_iq_id: state.primaryFields.cap_iq_id || null,
        sacra_domain: state.primaryFields.sacra_domain || null,
        zanbato_id: state.primaryFields.zanbato_id || null,
      },
      secondary_data: {
        name: state.secondaryFields.name,
        cap_iq_id: state.secondaryFields.cap_iq_id || null,
        sacra_domain: state.secondaryFields.sacra_domain || null,
        zanbato_id: state.secondaryFields.zanbato_id || null,
      },
      move_companies: state.moveCompanies === 'none' ? null : (state.moveCompanies as MergeProfileSave.move_companies),
      move_issuer_names: state.moveIssuerNames === 'none' ? null : (state.moveIssuerNames as MergeProfileSave.move_issuer_names),
      delete_secondary: state.deleteSecondary,
    };

    try {
      await mergeMutation.mutateAsync({ primaryId: leftProfile.id, payload });
      enqueueSnackbar('Profiles merged successfully', { variant: 'success' });
      onSuccess();
    } catch {
      enqueueSnackbar('Failed to merge profiles — please try again', { variant: 'error' });
    }
  };

  const moveLeft = state.moveCompanies === 'to_primary';
  const moveRight = state.moveCompanies === 'to_secondary';
  const moveIssuersLeft = state.moveIssuerNames === 'to_primary';
  const moveIssuersRight = state.moveIssuerNames === 'to_secondary';

  const allCompanies = [...leftCompanies, ...rightCompanies];
  let displayedLeftCompanies = leftCompanies;
  let displayedRightCompanies = rightCompanies;
  if (moveLeft) {
    displayedLeftCompanies = allCompanies;
    displayedRightCompanies = [];
  } else if (moveRight) {
    displayedLeftCompanies = [];
    displayedRightCompanies = allCompanies;
  }

  const allIssuers = [...leftIssuers, ...rightIssuers];
  let displayedLeftIssuers = leftIssuers;
  let displayedRightIssuers = rightIssuers;
  if (moveIssuersLeft) {
    displayedLeftIssuers = allIssuers;
    displayedRightIssuers = [];
  } else if (moveIssuersRight) {
    displayedLeftIssuers = [];
    displayedRightIssuers = allIssuers;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Merge Profiles</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} alignItems="center" sx={{ mt: 0.5 }}>

          {/* Header: profile names + swap button */}
          <Grid size={5}>
            <Typography variant="subtitle2" fontWeight="bold">
              {leftProfile.name}
            </Typography>
          </Grid>
          <Grid size={2} sx={{ textAlign: 'center' }}>
            <Button
              size="small"
              startIcon={<SwapHorizIcon />}
              onClick={handleSwap}
              title="Swap sides — the left profile is the one kept by default"
            >
              Swap
            </Button>
          </Grid>
          <Grid size={5}>
            <Typography variant="subtitle2" fontWeight="bold">
              {rightProfile.name}
            </Typography>
          </Grid>

          {/* Name row */}
          <Grid size={5}>
            <TextField
              label="Name"
              fullWidth
              size="small"
              value={state.primaryFields.name}
              onChange={e => setState(prev => ({ ...prev, primaryFields: { ...prev.primaryFields, name: e.target.value } }))}
            />
          </Grid>
          <Grid size={2} />
          <Grid size={5}>
            <TextField
              label="Name"
              fullWidth
              size="small"
              value={state.secondaryFields.name}
              onChange={e => setState(prev => ({ ...prev, secondaryFields: { ...prev.secondaryFields, name: e.target.value } }))}
            />
          </Grid>

          {/* Unique fields with transfer arrows */}
          {UNIQUE_FIELDS.map(field => {
            const isConflict = conflictFields.includes(field);
            return (
              <Grid
                container
                size={12}
                key={field}
                alignItems="center"
                spacing={1}
                sx={isConflict ? { backgroundColor: 'warning.light', borderRadius: 1, py: 0.5 } : {}}
              >
                <Grid size={5}>
                  <TextField
                    label={UNIQUE_FIELD_LABELS[field]}
                    fullWidth
                    size="small"
                    value={state.primaryFields[field as keyof typeof state.primaryFields]}
                    onChange={e =>
                      setState(prev => ({ ...prev, primaryFields: { ...prev.primaryFields, [field]: e.target.value } }))
                    }
                  />
                </Grid>
                <Grid size={2}>
                  <Box display="flex" justifyContent="center" gap={0.5}>
                    <IconButton size="small" onClick={() => takeLeft(field)} title="Copy value from right, clear right">
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => takeRight(field)} title="Copy value from left, clear left">
                      <ArrowForwardIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
                <Grid size={5}>
                  <TextField
                    label={UNIQUE_FIELD_LABELS[field]}
                    fullWidth
                    size="small"
                    value={state.secondaryFields[field as keyof typeof state.secondaryFields]}
                    onChange={e =>
                      setState(prev => ({ ...prev, secondaryFields: { ...prev.secondaryFields, [field]: e.target.value } }))
                    }
                  />
                </Grid>
              </Grid>
            );
          })}

          {/* Companies row */}
          <Grid container size={12} alignItems="center" spacing={1}>
            <Grid size={5}>
              <Tooltip
                title={displayedLeftCompanies.map(c => `${c.name} (${c.firm_name})`).join('\n')}
                placement="bottom-start"
                slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}
              >
                <Typography variant="body2" color={moveLeft ? 'primary' : 'text.secondary'} sx={{ cursor: 'default' }}>
                  {displayedLeftCompanies.length} {displayedLeftCompanies.length === 1 ? 'company' : 'companies'}
                </Typography>
              </Tooltip>
            </Grid>
            <Grid size={2}>
              <Box display="flex" justifyContent="center" gap={0.5}>
                <IconButton size="small" onClick={() => toggleMoveCompanies('to_primary')} color={moveLeft ? 'primary' : 'default'} disabled={state.deleteSecondary}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => toggleMoveCompanies('to_secondary')} color={moveRight ? 'primary' : 'default'} disabled={state.deleteSecondary}>
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </Box>
            </Grid>
            <Grid size={5}>
              <Tooltip
                title={displayedRightCompanies.map(c => `${c.name} (${c.firm_name})`).join('\n')}
                placement="bottom-start"
                slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}
              >
                <Typography variant="body2" color={moveRight ? 'primary' : 'text.secondary'} sx={{ cursor: 'default' }}>
                  {displayedRightCompanies.length} {displayedRightCompanies.length === 1 ? 'company' : 'companies'}
                </Typography>
              </Tooltip>
            </Grid>
          </Grid>

          {/* Issuer names row */}
          <Grid container size={12} alignItems="center" spacing={1}>
            <Grid size={5}>
              <Tooltip
                title={displayedLeftIssuers.map(i => `${i.edgar_name} (${i.fund_name})`).join('\n')}
                placement="bottom-start"
                slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}
              >
                <Typography variant="body2" color={moveIssuersLeft ? 'primary' : 'text.secondary'} sx={{ cursor: 'default' }}>
                  {displayedLeftIssuers.length} Edgar {displayedLeftIssuers.length === 1 ? 'issuer name' : 'issuer names'}
                </Typography>
              </Tooltip>
            </Grid>
            <Grid size={2}>
              <Box display="flex" justifyContent="center" gap={0.5}>
                <IconButton size="small" onClick={() => toggleMoveIssuerNames('to_primary')} color={moveIssuersLeft ? 'primary' : 'default'} disabled={state.deleteSecondary}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => toggleMoveIssuerNames('to_secondary')} color={moveIssuersRight ? 'primary' : 'default'} disabled={state.deleteSecondary}>
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </Box>
            </Grid>
            <Grid size={5}>
              <Tooltip
                title={displayedRightIssuers.map(i => `${i.edgar_name} (${i.fund_name})`).join('\n')}
                placement="bottom-start"
                slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}
              >
                <Typography variant="body2" color={moveIssuersRight ? 'primary' : 'text.secondary'} sx={{ cursor: 'default' }}>
                  {displayedRightIssuers.length} Edgar {displayedRightIssuers.length === 1 ? 'issuer name' : 'issuer names'}
                </Typography>
              </Tooltip>
            </Grid>
          </Grid>

        </Grid>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={state.deleteSecondary}
                onChange={e => {
                  const deleting = e.target.checked;
                  setState(prev => ({
                    ...prev,
                    deleteSecondary: deleting,
                    moveCompanies: deleting ? 'to_primary' : prev.moveCompanies,
                    moveIssuerNames: deleting ? 'to_primary' : prev.moveIssuerNames,
                  }));
                }}
              />
            }
            label={`Delete "${rightProfile.name}" after saving`}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mergeMutation.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={mergeMutation.isPending}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfileMergeDialog;
